import { Center, Spinner } from '@chakra-ui/react'
import { setUser } from '@sentry/react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import useFetch, { CachePolicies } from 'use-http'
import type { UserSessionResponse } from '../interfaces'
import type { PostTransformation } from '../interfaces/UserSessionResponse'
import type { UploadOptions } from 'imagekit-javascript/dist/src/interfaces/UploadOptions'
import { convertDefaultUploadParameters } from '../../utils/mediaLibrary'

export type ExtendedUploadOptions = Partial<UploadOptions> & {
  isPublished?: boolean
  overwriteDescription?: boolean
  transformation?: string
}

interface ContextProps {
  user: UserSessionResponse | null
  loading: boolean
  refetch(): Promise<UserSessionResponse> | Promise<void>
  defaultUploadOptions: ExtendedUploadOptions
}

export const UserContext = React.createContext<ContextProps>({
  user: null,
  loading: false,
  refetch: () => Promise.resolve(),
  defaultUploadOptions: {},
})

export const UserProvider = ({
  children,
}: {
  children: React.ReactElement
}): React.ReactElement => {
  const [initialLoad, setInitialLoad] = useState<boolean>(
    Boolean(
      window.user &&
        window.user.clientNumber &&
        window.user.imagekitId &&
        window.user.userEmail,
    ),
  )

  const validExtensionIdsRef = useRef<Set<string>>(new Set())
  const [extensionsLoaded, setExtensionsLoaded] = useState(false)

  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
    data: [],
  })

  const {
    data: loadedUser,
    loading,
    get,
  } = useFetch<UserSessionResponse>('/api/session/', {
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    cachePolicy: CachePolicies.NO_CACHE,
    onNewData: (currData, newData: UserSessionResponse) => {
      setInitialLoad(true)
      return newData
    },
    data: window?.user ? { ...window.user } : null,
  })

  const fetchUser = async (): Promise<void> => {
    const user = await get()
    if (window.user) {
      window.user = { ...window.user, ...user }
    }
    if (!(user && user.clientNumber && user.imagekitId && user.userEmail)) {
      window.location.href = `/logout?isMediaLibraryWidget=${
        window.isMediaLibraryWidget
      }${window.widgetHost ? `&widgetHost=${window.widgetHost}` : ''}
      ${
        window.widgetImagekitId
          ? `&widgetImagekitId=${window.widgetImagekitId}`
          : ''
      }`
    }
  }

  useEffect(() => {
    if (
      !(
        loadedUser &&
        loadedUser.clientNumber &&
        loadedUser.imagekitId &&
        loadedUser.userEmail
      )
    ) {
      void fetchUser()
    }
  }, [])

  useEffect(() => {
    if (loadedUser) {
      setUser({
        id: loadedUser.clientNumber,
        imagekitId: loadedUser.imagekitId,
        clientNumber: loadedUser.clientNumber,
        email: loadedUser.userEmail,
      })
    }
  }, [loadedUser])

  useEffect(() => {
    const fetchValidExtensions = async () => {
      if (!loadedUser?.clientNumber) {
        return
      }

      try {
        const response = await request.get('/api/v1/saved-extensions')
        if (response && Array.isArray(response)) {
          validExtensionIdsRef.current = new Set(
            response.map((ext: any) => ext.id),
          )
        }
      } catch (error) {
        console.error('Failed to fetch valid extensions:', error)
      }
      setExtensionsLoaded(true)
    }
    fetchValidExtensions()
  }, [loadedUser])

  const defaultUploadOptions = useMemo<ExtendedUploadOptions>(() => {
    if (!loadedUser) return {}

    const defaults = convertDefaultUploadParameters(loadedUser)
    const ext: ExtendedUploadOptions = {}

    if (Array.isArray(loadedUser.tags) && loadedUser.tags.length)
      ext.tags = loadedUser.tags.join(',')

    if (loadedUser.webhookUrl) ext.webhookUrl = loadedUser.webhookUrl

    if (defaults.extensions) {
      const filteredExtensions = Array.isArray(defaults.extensions)
        ? defaults.extensions.filter((extension: any) => {
            return (
              extension.name !== 'saved-extension' ||
              (extension.id && validExtensionIdsRef.current.has(extension.id))
            )
          })
        : defaults.extensions

      if (Array.isArray(filteredExtensions) && filteredExtensions.length > 0) {
        ext.extensions = filteredExtensions as unknown as object[]
      }
    }

    if (defaults.customCoordinates)
      ext.customCoordinates = defaults.customCoordinates
    if (defaults.customMetadata)
      ext.customMetadata = JSON.stringify(
        defaults.customMetadata as unknown as Record<
          string,
          string | number | boolean | Array<string | number | boolean>
        >,
      )

    const booleanProps = [
      'useUniqueFileName',
      'isPrivateFile',
      'overwriteFile',
      'overwriteAITags',
      'overwriteTags',
      'overwriteCustomMetadata',
      'overwriteDescription',
      'isPublished',
    ] as const

    booleanProps.forEach((prop) => {
      if (typeof defaults[prop] === 'boolean') {
        ext[prop] = defaults[prop]
      }
    })

    const transformation: { pre?: string; post?: PostTransformation[] } = {}
    if (defaults.preTransformation)
      transformation.pre = defaults.preTransformation
    if (
      Array.isArray(defaults.postTransformation)
        ? defaults.postTransformation.length > 0
        : Boolean(defaults.postTransformation)
    ) {
      transformation.post = defaults.postTransformation
    }
    if (Object.keys(transformation).length)
      ext.transformation = JSON.stringify(transformation)

    return ext
  }, [loadedUser, extensionsLoaded])

  return (
    <UserContext.Provider
      value={{
        loading,
        user: loadedUser ?? null,
        refetch: fetchUser,
        defaultUploadOptions,
      }}
    >
      {!initialLoad ? (
        <Center width="full" height="100vh">
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="appPaleGrey"
            color="appBlue"
            size="xl"
          />
        </Center>
      ) : (
        children
      )}
    </UserContext.Provider>
  )
}
